import { useEffect, useState, type FormEvent } from 'react';
import { useAuth } from '@/app/providers/AuthProvider';
import { nextActionsApi, type NextActionOptionDto } from '@/shared/api/nextActionsApi';
import type { LeadDto, UpsertLeadNoteRequest } from '@/shared/types/domain';
import { Button } from '@/shared/components/Button';
// RatingSelect import removed -- rating UI hidden 2026-11-04, see note below.
// import { RatingSelect } from './RatingSelect';

interface LeadNotesFormProps {
  lead: LeadDto;
  onSave: (data: UpsertLeadNoteRequest) => Promise<void>;
}

export function LeadNotesForm({ lead, onSave }: LeadNotesFormProps) {
  const { user } = useAuth();
  const [options, setOptions] = useState<NextActionOptionDto[]>([]);
  const [nextAction, setNextAction] = useState(lead.nextAction ?? '');
  const [notes, setNotes] = useState(lead.notes ?? '');
  // Rating is intentionally left wired here (state + still sent on save) even
  // though its input is hidden -- it stays populated in the leads export per
  // explicit instruction, this just removes the field from the edit UI.
  // To restore the UI: uncomment the RatingSelect import above and the
  // <RatingSelect ... /> usage below.
  const [rating] = useState(lead.rating ?? null);
  const [interestedProducts, setInterestedProducts] = useState(lead.interestedProducts ?? '');
  const [followUpDate, setFollowUpDate] = useState(
    lead.followUpDateUtc ? lead.followUpDateUtc.slice(0, 10) : ''
  );
  const [isSaving, setIsSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  useEffect(() => {
    if (user?.exhibitorId) {
      nextActionsApi.listByExhibitor(user.exhibitorId).then(setOptions);
    }
  }, [user?.exhibitorId]);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setIsSaving(true);

    try {
      await onSave({
        nextAction: nextAction || null,
        notes: notes.trim() || null,
        rating,
        interestedProducts: interestedProducts.trim() || null,
        followUpDateUtc: followUpDate ? new Date(followUpDate).toISOString() : null
      });
      setSavedAt(Date.now());
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
      {/* <RatingSelect value={rating} onChange={setRating} /> */}

      <div className="field">
        <label className="field__label" htmlFor="next-action">
          Next Action
        </label>
        <select
          id="next-action"
          className="field__input"
          value={nextAction}
          onChange={(e) => setNextAction(e.target.value)}
        >
          <option value="">— None selected —</option>
          {options.map((opt) => (
            <option key={opt.id} value={opt.label}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      <div className="field">
        <label className="field__label" htmlFor="notes">
          Notes
        </label>
        <textarea
          id="notes"
          className="field__input"
          style={{ minHeight: 96, paddingTop: 12, paddingBottom: 12, resize: 'vertical' }}
          maxLength={2000}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="What did you talk about?"
        />
      </div>

      <div className="field">
        <label className="field__label" htmlFor="products">
          Interested products
        </label>
        <input
          id="products"
          className="field__input"
          maxLength={500}
          value={interestedProducts}
          onChange={(e) => setInterestedProducts(e.target.value)}
          placeholder="e.g. Orthotics, Training"
        />
      </div>

      <div className="field">
        <label className="field__label" htmlFor="followup">
          Follow-up date
        </label>
        <input
          id="followup"
          type="date"
          className="field__input"
          value={followUpDate}
          onChange={(e) => setFollowUpDate(e.target.value)}
        />
      </div>

      <Button type="submit" isLoading={isSaving} fullWidth>
        {savedAt && !isSaving ? 'Saved ✓' : 'Save'}
      </Button>
    </form>
  );
}